/**
 * CountUpNumber — анимированный счётчик USDT-сумм.
 *
 * Катит цифры с ease-out 1.2с, tabular-nums (чтобы ширина не
 * прыгала при смене цифр), опционально с префиксом/суффиксом.
 *
 * Использует useMotionValue + animate(mv, ...) из motion/react —
 * react-friendly, не триггерит лишних ре-рендеров.
 *
 * Формат СНГ: пробел для тысяч, до 2 знаков после точки (USDT).
 */
import { animate, useMotionValue, useTransform, motion } from 'motion/react';
import { useEffect } from 'react';
import { COUNT_UP_DURATION, COUNT_UP_EASE } from './springs';

interface CountUpNumberProps {
  /** Целевое значение (число или numeric string) */
  value: number | string;
  /** Сколько знаков после точки (по умолчанию 2 для USDT) */
  decimals?: number;
  /** Префикс, например "$" или "+" */
  prefix?: string;
  /** Суффикс, например " USDT" */
  suffix?: string;
  /** Задержка старта (для stagger эффекта) */
  delay?: number;
  /** Отключить анимацию (для snapshot/теста) */
  instant?: boolean;
  /** Класс для обёртки — tracking, font-weight задаём снаружи */
  className?: string;
}

function formatRu(n: number, decimals: number): string {
  return n.toLocaleString('ru-RU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true,
  });
}

export function CountUpNumber({
  value,
  decimals = 2,
  prefix = '',
  suffix = '',
  delay = 0,
  instant = false,
  className = '',
}: CountUpNumberProps) {
  const target = typeof value === 'string' ? parseFloat(value) || 0 : value;
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) => `${prefix}${formatRu(v, decimals)}${suffix}`);

  useEffect(() => {
    if (instant) {
      mv.set(target);
      return;
    }
    const controls = animate(mv, target, {
      duration: COUNT_UP_DURATION,
      ease: COUNT_UP_EASE,
      delay,
    });
    return () => controls.stop();
  }, [target, delay, instant, mv]);

  return (
    <motion.span
      className={className}
      style={{
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '-0.015em',
      }}
    >
      {display}
    </motion.span>
  );
}
